import {
  AnalyticsEventName,
  formatAttendanceSessionForTracking,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import { RegisterComposition, useGetEligibleRegisterGroupsQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form-lts';

import { CoupledStructureConfirm } from './CoupledStructureConfirm';
import { RegisterCreateForm } from './useRegisterComposition';
import { RegisterCreateStep } from './useRegisterCreateModal';
import {
  RegisterGroupIds,
  RegisterGroupTarget,
  RegisterPeriod,
  RegisterScope,
  RegisterTab,
} from './types';
import { getCoupledStructurePeriodOptions, getCoupledTarget } from './utils/registerCoupling';
import { getGroupEntryCount } from './utils/registerCounts';
import { getAvailableRegisterTabs } from './utils/registerEligibility';
import {
  addGroupIdToTarget,
  getAutoSelectedGroupIds,
  removeGroupIdFromTarget,
} from './utils/registerGroupIds';
import { withCompositionSnapshot } from './utils/registerRoster';

type UseRegisterGroupSelectionParams = {
  form: UseFormReturn<RegisterCreateForm>;
  isEdit: boolean;
  step: RegisterCreateStep;
  date: string;
  composition: RegisterComposition | undefined;
  isSeeded: boolean;
  hasManualSelection: boolean;
  setHasManualSelection: (value: boolean) => void;
  selectedTab: RegisterTab | undefined;
  periods: RegisterPeriod[];
};

export const useRegisterGroupSelection = ({
  form,
  isEdit,
  step,
  date,
  composition,
  isSeeded,
  hasManualSelection,
  setHasManualSelection,
  selectedTab,
  periods,
}: UseRegisterGroupSelectionParams) => {
  const { schoolId = '' } = useAuth();
  const { getConfirmation } = useConfirmationDialog();
  const { activeAgeGroups } = useAgeGroups(
    { schoolId, userType: SchoolUserRole.Student },
    { enabled: !!schoolId },
  );

  const formGroupIds = form.watch('group_ids');

  const {
    data: eligibleGroups = [],
    isLoading: eligibleGroupsLoading,
    isFetching: eligibleGroupsFetching,
  } = useGetEligibleRegisterGroupsQuery(
    { schoolId, registerDate: date },
    {
      enabled: step === 'groups' && !!date && !!schoolId && isSeeded,
      refetchOnMount: 'always',
    },
  );

  const selectedGroupIds = useMemo(() => {
    if (isEdit || hasManualSelection) return formGroupIds;
    return getAutoSelectedGroupIds(eligibleGroups);
  }, [formGroupIds, hasManualSelection, eligibleGroups, isEdit]);

  const rosterGroups = useMemo(
    () => withCompositionSnapshot(eligibleGroups, composition?.groups ?? []),
    [eligibleGroups, composition],
  );

  const addedGroups = useMemo(
    () =>
      rosterGroups.filter(
        (group) =>
          selectedGroupIds.am.includes(group.group_id) ||
          selectedGroupIds.pm.includes(group.group_id) ||
          Object.values(selectedGroupIds.periods).some((ids) => ids.includes(group.group_id)),
      ),
    [rosterGroups, selectedGroupIds],
  );

  const availableTabs = useMemo(
    () => getAvailableRegisterTabs(rosterGroups, activeAgeGroups),
    [activeAgeGroups, rosterGroups],
  );

  const isSelectedTabAvailable = !!selectedTab && availableTabs.includes(selectedTab);
  const activeTab = isSelectedTabAvailable ? selectedTab : availableTabs[0];

  const getCurrentGroupIds = useCallback((): RegisterGroupIds => {
    if (isEdit || hasManualSelection) return form.getValues('group_ids');
    return getAutoSelectedGroupIds(eligibleGroups);
  }, [form, isEdit, hasManualSelection, eligibleGroups]);

  const addGroupIdInForm = useCallback(
    (id: string, target: RegisterGroupTarget) => {
      setHasManualSelection(true);
      form.setValue(
        'group_ids',
        addGroupIdToTarget({ groupIds: getCurrentGroupIds(), id, target }),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    },
    [form, getCurrentGroupIds, setHasManualSelection],
  );

  const trackGroupChange = useCallback(
    (
      name: AnalyticsEventName.AttendanceAddGroup | AnalyticsEventName.AttendanceRemoveGroup,
      target: RegisterGroupTarget,
    ) => {
      const periodOrder = target.periodId
        ? periods.find((period) => period.id === target.periodId)?.order
        : undefined;
      const value = formatAttendanceSessionForTracking(
        target.session ?? null,
        periodOrder != null ? [periodOrder] : [],
      );

      if (!value) return;

      trackAmplitudeEvent({
        name,
        payload: { session: value, action: isEdit ? 'edit' : 'create' },
      });
    },
    [isEdit, periods],
  );

  const addGroupId = useCallback(
    async (id: string, scope: RegisterScope) => {
      const group = eligibleGroups.find((item) => item.group_id === id);
      const groupIds = getCurrentGroupIds();

      if (!group || !('session' in scope)) {
        const target = group ? getCoupledTarget({ group, groupIds, scope, periods }) : scope;
        addGroupIdInForm(id, target);
        trackGroupChange(AnalyticsEventName.AttendanceAddGroup, target);
        return;
      }

      const options = getCoupledStructurePeriodOptions({
        group,
        groupIds,
        session: scope.session,
        periods,
      });
      let periodId = options[0]?.id;

      if (options.length > 1) {
        let selectedPeriodId = periodId;
        const confirmed = await getConfirmation({
          textId: 'attendance-CoupledGroupStructureConfirmation',
          textValues: { name: group.name },
          content: (
            <CoupledStructureConfirm
              options={options}
              onChange={(nextPeriodId) => {
                selectedPeriodId = nextPeriodId;
              }}
            />
          ),
        });

        if (!confirmed) return;
        periodId = selectedPeriodId;
      }

      const target = { session: scope.session, periodId };
      addGroupIdInForm(id, target);
      trackGroupChange(AnalyticsEventName.AttendanceAddGroup, target);
    },
    [
      addGroupIdInForm,
      eligibleGroups,
      getConfirmation,
      getCurrentGroupIds,
      periods,
      trackGroupChange,
    ],
  );

  const removeGroupId = useCallback(
    async (id: string, scope: RegisterScope) => {
      const group = eligibleGroups.find((item) => item.group_id === id);
      const target = group
        ? getCoupledTarget({ group, groupIds: getCurrentGroupIds(), scope, periods })
        : scope;
      const entriesCount = composition
        ? getGroupEntryCount({ composition, groupId: id, target })
        : 0;
      if (entriesCount > 0) {
        const confirmed = await getConfirmation({
          textId: 'attendance-RegisterUpdateConfirmation',
          textValues: { entriesCount },
        });
        if (!confirmed) return;
      }

      setHasManualSelection(true);
      form.setValue(
        'group_ids',
        removeGroupIdFromTarget({ groupIds: getCurrentGroupIds(), id, target }),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      trackGroupChange(AnalyticsEventName.AttendanceRemoveGroup, target);
    },
    [
      composition,
      form,
      getConfirmation,
      getCurrentGroupIds,
      setHasManualSelection,
      eligibleGroups,
      periods,
      trackGroupChange,
    ],
  );

  return {
    eligibleGroups,
    eligibleGroupsLoading,
    eligibleGroupsFetching,
    selectedGroupIds,
    addedGroups,
    availableTabs,
    activeTab,
    addGroupId,
    removeGroupId,
  };
};
